import Container from '@ui/container/container';
import SwitchV2 from '@ui/switch-v2/switch-v2';
import Text from '@ui/text/text/text';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import { useCallback, useMemo } from 'react';
import { ISSOIntegration, RegistrationStep, RegistrationStepType, SingleSignOnTypes } from '@/types/registration.types';
import { GetDefaultRegistrationQuestions } from '@/utils/registration/registration';
import { saveWorkingProjectByFields, updateRegistrationSteps } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import useLoadSSOs from '../../hooks/use-load-ssos';
import Switch from '@ui/switch/switch';
import OptionalComponent from '@ui/optional-component/optional-component';
import Image from '@ui/image/image';
import images from '@/utils/images';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';

interface SignInOption {
  type: SingleSignOnTypes;
  label: string;
  icon: string;
  uuid?: string;
}

const SignInOptions: SignInOption[] = [{ type: SingleSignOnTypes.Google, label: 'Google', icon: images.Google ?? '' }];

export default function SSORegistrationSettings() {
  const dispatch = useAppDispatch();
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);
  const { ssos, publicOauthIntegration } = useLoadSSOs();
  const { singleSignOnEnabled, passwordGateEnabled, courseOn, registrationSteps, registrationSettings } =
    useComputedRegistrationSettings();

  const displayBeforeGeneralInfoQuestions = registrationSettings?.singleSignOn?.displayTop;

  const updatedSteps = useCallback(() => {
    // custom SSO excludes registration gating and all registration steps except general (which includes only first name, last name, and email)
    // first and last will become optional which are set inside the updateRegistrationSteps action using the makeNameOptional flag
    // note: if breakout session or leaderboard is on, then the first and last name will remain required; see registration-required-fields.ts -> useSetRequiredField
    const _updatedSteps: RegistrationStep[] = registrationSteps?.map((step: RegistrationStep) => {
      if (step.type === RegistrationStepType.general) {
        return {
          ...step,
          questions: GetDefaultRegistrationQuestions(),
        };
      }
      return step;
    });

    return _updatedSteps;
  }, [registrationSteps]);

  const customSignInOptions: SignInOption[] = useMemo(() => {
    return (
      ssos
        ?.filter((sso: ISSOIntegration) => sso.enabled)
        .map((sso: ISSOIntegration) => {
          return { type: sso.type as SingleSignOnTypes, label: sso.login_text, icon: sso.icon, uuid: sso.uuid };
        }) ?? []
    );
  }, [ssos]);

  const oauthSignInOption: SignInOption | null = useMemo(() => {
    return publicOauthIntegration
      ? {
          type: SingleSignOnTypes.oAuth,
          label: publicOauthIntegration.login_text || '',
          icon: publicOauthIntegration.icon || '',
        }
      : null;
  }, [publicOauthIntegration]);

  const handleToggleSSO = (value: boolean, on: boolean) => {
    if (!registrationSettings) return;
    if (on) {
      dispatch(updateRegistrationSteps({ steps: updatedSteps(), options: { makeNameOptional: true } }));
    }
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, isOn: on },
        },
      })
    );
  };

  const handleToggleSignInOption = (value: string, on: boolean) => {
    if (!registrationSettings) return;
    // Only 1 can be on at a time, if it's already on, turn it off
    const singleSignOns = on ? [value] : [];
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: {
            ...registrationSettings?.singleSignOn,
            singleSignOns,
          },
        },
      })
    );
  };

  const handleToggleDisplayBeforeGeneralInfoQuestions = (value: boolean, on: boolean) => {
    if (!registrationSettings) return;
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, displayTop: on },
        },
      })
    );
  };

  function renderSignInOption(option: SignInOption) {
    const value = option.uuid ?? option.type;
    const isOn = registrationSettings?.singleSignOn?.singleSignOns?.includes(value) ?? false;
    return (
      <Container
        design="GlobalCont2"
        className="flex fd-row jc-space-between ai-center"
        borderRadius="12px"
        padding="16px"
        key={value}
      >
        <label className="flex fd-row ai-center gap-8">
          {!!option.icon && <Image width={24} height={24} src={option.icon} alt="" />}
          <Text clamp={1}>Sign in with {option.label}</Text>
        </label>
        <Switch disabled={savingWorkingProject} value={value} on={isOn} onClick={handleToggleSignInOption} />
      </Container>
    );
  }

  return (
    <Container design="GlobalCont1" className="flex fd-column gap-8" borderRadius="12px" padding="16px">
      <div className="flex fd-row jc-space-between ai-center">
        <Text tag="h6" className="H6">
          Single Sign On
        </Text>
        <div>
          <SwitchV2
            value={singleSignOnEnabled}
            loading={savingWorkingProject}
            on={singleSignOnEnabled}
            onClick={handleToggleSSO}
            disabled={passwordGateEnabled || courseOn}
          />
        </div>
      </div>
      <OptionalComponent display={singleSignOnEnabled}>
        <div className="flex fd-column gap-8">
          {customSignInOptions.map(renderSignInOption)}
          {oauthSignInOption && renderSignInOption(oauthSignInOption)}
        </div>
        {/* Don't show social sign in header if channel has no SSO integrations because it looks weird */}
        <OptionalComponent display={!!(customSignInOptions.length > 0 || oauthSignInOption)}>
          <Text tag="h6" className="H6">
            Social Sign In
          </Text>
        </OptionalComponent>
        <Container design="GlobalCont2" borderRadius="12px">
          {SignInOptions.map(renderSignInOption)}
        </Container>
        <Container
          design="GlobalCont2"
          className="flex fd-row jc-space-between ai-center"
          borderRadius="12px"
          padding="16px"
        >
          <label className="flex fd-row ai-center gap-8">
            <Text clamp={1}>Display before general info questions</Text>
          </label>
          <Switch
            value={!!displayBeforeGeneralInfoQuestions}
            disabled={savingWorkingProject}
            on={!!displayBeforeGeneralInfoQuestions}
            onClick={handleToggleDisplayBeforeGeneralInfoQuestions}
          />
        </Container>
      </OptionalComponent>
    </Container>
  );
}
